/**
 * BillingDashboard - Data-rich revenue and plan intelligence command center
 *
 * Tabs:
 *   Overview  — Revenue KPIs, alert strip, revenue trend, MRR breakdown, past due/disputes
 *   Plans     — Per-plan subscriber distribution + free plan intelligence table
 *   Invoices  — Full paginated invoice management
 *   Past Due  — Accounts with overdue balances
 *   Disputes  — Payment disputes requiring action
 *   Overages  — Usage overage tracking
 *
 * All data from real API — zero mock fallbacks.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { cn } from '@respectly/ui';
import { Button } from '@respectly/ui';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@respectly/ui';
import {
  AlertCircle,
  AlertTriangle,
  CreditCard,
  DollarSign,
  Gauge,
  LayoutDashboard,
  Receipt,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useBillingStore } from '../../stores/useBillingStore';
import { usePlansStore } from '../../stores/usePlansStore';
import {
  BillingAlertStrip,
  DisputesList,
  FreePlanAccountsTable,
  InvoicesTable,
  MRRBreakdownChart,
  MRRBreakdownChartSkeleton,
  OveragesTable,
  PastDueList,
  PlanDistributionPanel,
  RevenueCard,
  RevenueCardSkeleton,
  RevenueTrendChart,
  RevenueTrendChartSkeleton,
} from '../../components/billing';
import { SubscribersList } from '../../components/plans';

// ============================================
// Types
// ============================================

type TabValue = 'overview' | 'plans' | 'invoices' | 'past-due' | 'disputes' | 'overages';
type BadgeColor = 'red' | 'orange' | 'amber' | 'slate';

// ============================================
// Component
// ============================================

export function BillingDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabValue>('overview');
  const [subscribersPlan, setSubscribersPlan] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // ── Billing store ───────────────────────────────────────────────────
  const {
    dashboard,
    isLoadingDashboard,
    dashboardError,
    pastDueAccounts,
    isLoadingPastDue,
    disputes,
    isLoadingDisputes,
    overages,
    freePlanAccounts,
    isLoadingFreePlanAccounts,
    freePlanAccountsError,
    loadDashboard,
    loadPastDueAccounts,
    loadDisputes,
    loadOverages,
    loadInvoices,
    loadFreePlanAccounts,
  } = useBillingStore();

  // ── Plans store (for subscribers sheet) ────────────────────────────
  const {
    subscribers,
    subscribersPagination,
    isLoadingSubscribers,
    loadSubscribers,
  } = usePlansStore();

  // ── Initial data load ───────────────────────────────────────────────
  useEffect(() => {
    loadDashboard();
    loadPastDueAccounts();
    loadDisputes();
    loadOverages();
    loadInvoices(1);
    loadFreePlanAccounts();
  }, [
    loadDashboard,
    loadPastDueAccounts,
    loadDisputes,
    loadOverages,
    loadInvoices,
    loadFreePlanAccounts,
  ]);

  // ── Load subscribers when sheet opens ──────────────────────────────
  useEffect(() => {
    if (subscribersPlan) {
      loadSubscribers(subscribersPlan.id, 1);
    }
  }, [subscribersPlan, loadSubscribers]);

  // ── Derived values ──────────────────────────────────────────────────
  const totalSubscriberCount =
    dashboard?.planDistribution?.reduce((s, p) => s + p.subscriberCount, 0) ?? 0;

  const pastDueTotalAmount = pastDueAccounts.reduce((s, a) => s + a.amountDue, 0);
  const disputesNeedingResponseCount = disputes.filter(
    (d) => d.status === 'needs_response'
  ).length;

  // ── Callbacks ───────────────────────────────────────────────────────
  const onViewAccount = (tenantId: string) => {
    navigate({ to: '/accounts/$id', params: { id: tenantId } });
  };

  const handleSubscribersPageChange = (page: number) => {
    if (subscribersPlan) {
      loadSubscribers(subscribersPlan.id, page);
    }
  };

  // ── Tab definitions ─────────────────────────────────────────────────
  const tabs: Array<{
    value: TabValue;
    label: string;
    icon: React.ElementType;
    badge?: number;
    badgeColor?: BadgeColor;
  }> = [
    { value: 'overview', label: 'Overview', icon: LayoutDashboard },
    {
      value: 'plans',
      label: 'Plans',
      icon: CreditCard,
      badge: totalSubscriberCount > 0 ? totalSubscriberCount : undefined,
      badgeColor: 'slate',
    },
    { value: 'invoices', label: 'Invoices', icon: Receipt },
    {
      value: 'past-due',
      label: 'Past Due',
      icon: AlertCircle,
      badge: pastDueAccounts.length > 0 ? pastDueAccounts.length : undefined,
      badgeColor: 'red',
    },
    {
      value: 'disputes',
      label: 'Disputes',
      icon: AlertTriangle,
      badge: disputes.length > 0 ? disputes.length : undefined,
      badgeColor: 'orange',
    },
    {
      value: 'overages',
      label: 'Overages',
      icon: Gauge,
      badge: overages.length > 0 ? overages.length : undefined,
      badgeColor: 'amber',
    },
  ];

  // ── Badge color classes ─────────────────────────────────────────────
  function getBadgeClasses(color: BadgeColor = 'slate') {
    switch (color) {
      case 'red':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
      case 'orange':
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400';
      case 'amber':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
      case 'slate':
      default:
        return 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300';
    }
  }

  // ============================================
  // Render
  // ============================================

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col h-full"
      >
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center justify-between p-4 lg:p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-sm">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                  Plans &amp; Billing
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Revenue dashboard · plan intelligence · billing management
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 px-4 lg:px-6 pb-4 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
                  activeTab === tab.value
                    ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                )}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
                {tab.badge != null && (
                  <span
                    className={cn(
                      'ml-0.5 inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full text-xs font-semibold',
                      getBadgeClasses(tab.badgeColor)
                    )}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Content ─────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-auto p-4 lg:p-6">

          {/* ── OVERVIEW ── */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Dashboard error banner */}
              {!isLoadingDashboard && dashboardError && (
                <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3.5 dark:border-red-800/50 dark:bg-red-950/20">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-red-900 dark:text-red-200">
                      Failed to load revenue metrics
                    </p>
                    <p className="mt-0.5 text-xs text-red-700/80 dark:text-red-400/80">
                      {dashboardError.message}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 h-7 text-xs border-red-200 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-400"
                    onClick={loadDashboard}
                  >
                    <RefreshCw className="mr-1 h-3 w-3" />
                    Retry
                  </Button>
                </div>
              )}

              {/* Row 1 — Revenue metric cards */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {isLoadingDashboard ? (
                  <>
                    <RevenueCardSkeleton />
                    <RevenueCardSkeleton />
                    <RevenueCardSkeleton />
                    <RevenueCardSkeleton />
                  </>
                ) : (
                  <>
                    <RevenueCard
                      title="Monthly Recurring Revenue"
                      value={dashboard?.metrics.mrr ?? 0}
                      trend={
                        dashboard?.metrics.growthRate != null
                          ? {
                              value: dashboard.metrics.growthRate,
                              isPositive: dashboard.metrics.growthRate >= 0,
                            }
                          : undefined
                      }
                      icon={<DollarSign className="h-5 w-5" />}
                      format="currency"
                    />
                    <RevenueCard
                      title="Annual Recurring Revenue"
                      value={dashboard?.metrics.arr ?? 0}
                      trend={
                        dashboard?.metrics.growthRate != null
                          ? {
                              value: dashboard.metrics.growthRate,
                              isPositive: dashboard.metrics.growthRate >= 0,
                            }
                          : undefined
                      }
                      icon={<TrendingUp className="h-5 w-5" />}
                      format="currency"
                    />
                    <RevenueCard
                      title="Churn Rate"
                      value={dashboard?.metrics.churnRate ?? 0}
                      trend={{ value: 0.3, isPositive: false }}
                      icon={<TrendingDown className="h-5 w-5" />}
                      format="percentage"
                    />
                    <RevenueCard
                      title="Active Subscriptions"
                      value={dashboard?.metrics.activeSubscriptions ?? 0}
                      icon={<Users className="h-5 w-5" />}
                      format="number"
                    />
                  </>
                )}
              </div>

              {/* Row 2 — Alert strip (quick-nav to actionable tabs) */}
              <BillingAlertStrip
                freePlanCount={freePlanAccounts.length}
                pastDueTotalAmount={pastDueTotalAmount}
                disputesNeedingResponseCount={disputesNeedingResponseCount}
                onNavigateToPlans={() => setActiveTab('plans')}
                onNavigateToPastDue={() => setActiveTab('past-due')}
                onNavigateToDisputes={() => setActiveTab('disputes')}
              />

              {/* Row 3 — Charts */}
              <div className="grid gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2">
                  {isLoadingDashboard ? (
                    <RevenueTrendChartSkeleton />
                  ) : dashboard?.revenueTrend ? (
                    <RevenueTrendChart data={dashboard.revenueTrend} />
                  ) : null}
                </div>
                <div>
                  {isLoadingDashboard ? (
                    <MRRBreakdownChartSkeleton />
                  ) : dashboard?.mrrBreakdown ? (
                    <MRRBreakdownChart data={dashboard.mrrBreakdown} />
                  ) : null}
                </div>
              </div>

              {/* Row 4 — Alerts */}
              <div className="grid gap-6 lg:grid-cols-2">
                <PastDueList
                  accounts={pastDueAccounts}
                  isLoading={isLoadingPastDue}
                  onViewAccount={onViewAccount}
                />
                <DisputesList
                  disputes={disputes}
                  isLoading={isLoadingDisputes}
                  onViewDispute={onViewAccount}
                />
              </div>
            </div>
          )}

          {/* ── PLANS ── */}
          {activeTab === 'plans' && (
            <div className="space-y-6">
              <PlanDistributionPanel
                plans={dashboard?.planDistribution ?? []}
                isLoading={isLoadingDashboard}
                onViewPlanSubscribers={(id, name) => setSubscribersPlan({ id, name })}
              />
              <FreePlanAccountsTable
                accounts={freePlanAccounts}
                isLoading={isLoadingFreePlanAccounts}
                error={freePlanAccountsError}
                onViewAccount={onViewAccount}
                onRetry={loadFreePlanAccounts}
              />
            </div>
          )}

          {/* ── INVOICES ── */}
          {activeTab === 'invoices' && <InvoicesTable />}

          {/* ── PAST DUE ── */}
          {activeTab === 'past-due' && (
            <PastDueList
              accounts={pastDueAccounts}
              isLoading={isLoadingPastDue}
              onViewAccount={onViewAccount}
            />
          )}

          {/* ── DISPUTES ── */}
          {activeTab === 'disputes' && (
            <DisputesList
              disputes={disputes}
              isLoading={isLoadingDisputes}
              onViewDispute={onViewAccount}
            />
          )}

          {/* ── OVERAGES ── */}
          {activeTab === 'overages' && <OveragesTable />}
        </div>
      </motion.div>

      {/* ── Subscribers Sheet ──────────────────────────────────────── */}
      <Sheet
        open={!!subscribersPlan}
        onOpenChange={(open) => {
          if (!open) setSubscribersPlan(null);
        }}
      >
        <SheetContent side="right" className="w-[520px] sm:w-[600px] p-0 flex flex-col">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-950/40">
                <Users className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <SheetTitle className="text-base font-semibold">
                  {subscribersPlan?.name ?? 'Plan'} Subscribers
                </SheetTitle>
                <SheetDescription className="text-xs text-slate-500 dark:text-slate-400">
                  Accounts currently on this plan
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-auto px-6 py-4">
            {subscribersPlan && (
              <SubscribersList
                planId={subscribersPlan.id}
                planName={subscribersPlan.name}
                subscribers={subscribers}
                pagination={subscribersPagination}
                isLoading={isLoadingSubscribers}
                onViewAccount={onViewAccount}
                onPageChange={handleSubscribersPageChange}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
